import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import ChatTypePairs from '../components/ChatTypePairs';
import PublicPageShell from '../components/PublicPageShell';
import { AI_RAMS_DISCLAIMER } from '../components/LegalBusinessDetails';

// Public assets from /public
const LOGO_SRC = '/struxor.png';
const HERO_BG = '/background_image.jpg';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Feature = ({ icon: Icon, title, children }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        height: '100%',
        borderRadius: 4,
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 24px rgba(0,0,0,0.08)' },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Icon sx={{ fontSize: { xs: 32, md: 36 }, color: 'primary.main' }} />
        </Box>
        <Typography 
          variant="h6" 
          align="center" 
          gutterBottom
          sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          align="center" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' }, lineHeight: 1.5 }}
        >
          {children}
        </Typography>
      </CardContent>
    </Card>
  );
};

const mcl = { bg: '#fff', border: '#e5e7eb', muted: '#94a3b8', text: '#1e293b', sub: '#64748b', primary: '#0087c1', green: '#16a34a', amber: '#d97706' };
const mockupFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/* ── Timeline Dashboard mockup ── */
const TimelineDashboardPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mcl.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mcl.border}` }}>
      <Box sx={{ fontWeight: 800, color: mcl.primary, fontSize: 14 }}>struxor</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ px: 1.5, py: 0.5, background: '#f1f5f9', borderRadius: 1, fontSize: 11, color: mcl.muted }}>Search projects…</Box>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mcl.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
      </Box>
    </Box>
    <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ fontSize: 13, fontWeight: 700 }}>Timeline</Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Box sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#e0f2fe', color: mcl.primary, cursor: 'pointer' }}>Week</Box>
        <Box sx={{ fontSize: 10, fontWeight: 500, px: 1, py: 0.3, borderRadius: 1, color: mcl.muted, cursor: 'pointer' }}>Month</Box>
      </Box>
    </Box>
    <Box sx={{ px: 2, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ fontSize: 11, color: mcl.sub, cursor: 'pointer' }}>← Prev</Box>
      <Box sx={{ fontSize: 12, fontWeight: 600 }}>10 – 14 Mar 2026</Box>
      <Box sx={{ fontSize: 11, color: mcl.sub, cursor: 'pointer' }}>Next →</Box>
    </Box>
    {/* Day columns */}
    <Box sx={{ px: 2, pt: 1, pb: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
        {['Mon 10', 'Tue 11', 'Wed 12', 'Thu 13', 'Fri 14'].map(d => (
          <Box key={d} sx={{ textAlign: 'center', fontSize: 9, fontWeight: 600, color: mcl.muted, textTransform: 'uppercase', py: 0.5 }}>{d}</Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, minHeight: 140 }}>
        {/* Mon */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mcl.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mcl.primary }}>Fencing install</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Riverside Dev</Box>
          </Box>
          <Box sx={{ background: '#fef3c7', borderLeft: '3px solid #d97706', borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#92400e' }}>Safety audit</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Elm Court</Box>
          </Box>
        </Box>
        {/* Tue */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mcl.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mcl.primary }}>Fencing install</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Riverside Dev</Box>
          </Box>
        </Box>
        {/* Wed */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dcfce7', borderLeft: `3px solid ${mcl.green}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#166534' }}>Hoarding delivery</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Park Lane Ph2</Box>
          </Box>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mcl.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mcl.primary }}>Groundworks</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Elm Court</Box>
          </Box>
        </Box>
        {/* Thu */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#f3e8ff', borderLeft: '3px solid #7c3aed', borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#5b21b6' }}>Client meeting</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Riverside Dev</Box>
          </Box>
        </Box>
        {/* Fri */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ background: '#dbeafe', borderLeft: `3px solid ${mcl.primary}`, borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: mcl.primary }}>Final inspection</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Park Lane Ph2</Box>
          </Box>
          <Box sx={{ background: '#fef3c7', borderLeft: '3px solid #d97706', borderRadius: '0 4px 4px 0', px: 0.8, py: 0.5 }}>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#92400e' }}>RAMS sign-off</Box>
            <Box sx={{ fontSize: 8, color: mcl.sub }}>Elm Court</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

/* ── AI Quote Review mockup ── */
const AIQuoteReviewPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mcl.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mcl.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontWeight: 600, fontSize: 13 }}>AI Quote Review</Box>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 0.8, py: 0.2, borderRadius: 1, background: '#f3e8ff', color: '#7c3aed' }}>AI</Box>
      </Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mcl.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <Box sx={{ fontSize: 11, color: mcl.muted, mb: 0.5 }}>Reviewing: <span style={{ fontWeight: 600, color: mcl.text }}>P-1042 · Perimeter Fencing</span></Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#dcfce7', color: '#166534' }}>3 Suggestions</Box>
        <Box sx={{ fontSize: 9, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1, background: '#fef3c7', color: '#92400e' }}>1 Warning</Box>
      </Box>
    </Box>
    {/* Review items */}
    <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ border: '1px solid #bbf7d0', borderRadius: 2, p: 1.5, background: '#f0fdf4' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Box sx={{ fontSize: 12 }}>✓</Box>
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#166534' }}>Rate check passed</Box>
        </Box>
        <Box sx={{ fontSize: 10, color: mcl.sub, lineHeight: 1.5 }}>V-Mesh Weldmesh at £80/m is within the typical range (£70–£95/m) for 2.0m high panels.</Box>
      </Box>
      <Box sx={{ border: '1px solid #fde68a', borderRadius: 2, p: 1.5, background: '#fffbeb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Box sx={{ fontSize: 12 }}>⚠</Box>
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#92400e' }}>UPVC Hoarding rate high</Box>
        </Box>
        <Box sx={{ fontSize: 10, color: mcl.sub, lineHeight: 1.5 }}>£160/m is above typical range (£110–£145/m). Consider reviewing or adding a note justifying the premium.</Box>
      </Box>
      <Box sx={{ border: '1px solid #bbf7d0', borderRadius: 2, p: 1.5, background: '#f0fdf4' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Box sx={{ fontSize: 12 }}>💡</Box>
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#166534' }}>Missing item suggestion</Box>
        </Box>
        <Box sx={{ fontSize: 10, color: mcl.sub, lineHeight: 1.5 }}>Consider adding a line item for concrete foundations / post bases — commonly required with V-Mesh installations.</Box>
      </Box>
    </Box>
  </Box>
);

/* ── Pins on Plans mockup ── */
const PinsOnPlansPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mcl.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mcl.border}` }}>
      <Box sx={{ fontSize: 11, color: mcl.muted }}>Plans / <span style={{ color: mcl.text, fontWeight: 600 }}>Site Layout — Block A</span></Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mcl.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Plan area */}
    <Box sx={{ position: 'relative', background: '#f8fafc', height: 200, mx: 2, mt: 1.5, mb: 1, borderRadius: 2, border: `1px solid ${mcl.border}`, overflow: 'hidden' }}>
      {/* Grid lines to simulate a plan drawing */}
      {[40, 80, 120, 160].map(y => (
        <Box key={`h${y}`} sx={{ position: 'absolute', top: y, left: 0, right: 0, height: '1px', background: '#e2e8f0' }} />
      ))}
      {[60, 120, 180, 240].map(x => (
        <Box key={`v${x}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: x, width: '1px', background: '#e2e8f0' }} />
      ))}
      {/* Building outlines */}
      <Box sx={{ position: 'absolute', top: 30, left: 30, width: 100, height: 70, border: '2px solid #94a3b8', borderRadius: 1, background: 'rgba(148,163,184,0.08)' }}>
        <Box sx={{ fontSize: 8, color: mcl.muted, p: 0.5, textAlign: 'center', mt: 2 }}>Block A</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 60, left: 170, width: 80, height: 90, border: '2px solid #94a3b8', borderRadius: 1, background: 'rgba(148,163,184,0.08)' }}>
        <Box sx={{ fontSize: 8, color: mcl.muted, p: 0.5, textAlign: 'center', mt: 2 }}>Block B</Box>
      </Box>
      {/* Pins */}
      <Box sx={{ position: 'absolute', top: 42, left: 60, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mcl.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', cursor: 'pointer' }}>✓</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 80, left: 110, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mcl.amber, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', cursor: 'pointer' }}>!</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 100, left: 200, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: mcl.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', cursor: 'pointer' }}>3</Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 140, left: 50, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', cursor: 'pointer' }}>!</Box>
      </Box>
      {/* Selected pin popover */}
      <Box sx={{ position: 'absolute', top: 52, left: 120, background: '#fff', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', p: 1.2, width: 150, zIndex: 3, border: `1px solid ${mcl.border}` }}>
        <Box sx={{ fontSize: 10, fontWeight: 600, mb: 0.3 }}>Fencing install — Sec A</Box>
        <Box sx={{ fontSize: 9, color: mcl.muted, mb: 0.5 }}>Due: 12 Mar · 2 photos</Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Box sx={{ fontSize: 8, fontWeight: 600, px: 0.8, py: 0.2, borderRadius: 1, background: '#fef3c7', color: '#92400e' }}>In Progress</Box>
        </Box>
      </Box>
    </Box>
    {/* Legend */}
    <Box sx={{ display: 'flex', gap: 2, px: 2, pb: 1.5, pt: 0.5 }}>
      {[
        { color: mcl.green, label: 'Completed' },
        { color: mcl.amber, label: 'In Progress' },
        { color: '#dc2626', label: 'Overdue' },
        { color: mcl.primary, label: 'Activity' },
      ].map(l => (
        <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: l.color }} />
          <Box sx={{ fontSize: 9, color: mcl.muted }}>{l.label}</Box>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ── Clock In mockup ── */
const ClockInPreview = () => (
  <Box sx={{ fontFamily: mockupFont, fontSize: 12, color: mcl.text }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${mcl.border}` }}>
      <Box sx={{ fontWeight: 600, fontSize: 13 }}>My Time</Box>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: mcl.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>LR</Box>
    </Box>
    {/* Clock status card */}
    <Box sx={{ mx: 2, mt: 1.5, borderRadius: 2, border: `1px solid ${mcl.border}`, background: '#fafbfc', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: mcl.green, boxShadow: '0 0 0 3px rgba(22,163,74,0.2)' }} />
          <Box>
            <Box sx={{ fontSize: 12, fontWeight: 600 }}>Clocked in</Box>
            <Box sx={{ fontSize: 10, color: mcl.muted }}>Since 07:45 AM</Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: mcl.primary, lineHeight: 1 }}>4h 32m</Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', borderTop: `1px solid ${mcl.border}` }}>
        <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, fontSize: 11, fontWeight: 600, color: '#dc2626', cursor: 'pointer', background: '#fef2f2' }}>Clock Out</Box>
      </Box>
    </Box>
    {/* Today's entries */}
    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
      <Box sx={{ fontSize: 11, fontWeight: 600, color: mcl.sub, mb: 1 }}>Today — Friday 7 Mar</Box>
    </Box>
    <Box sx={{ px: 2, pb: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.6fr 0.6fr', gap: 1, py: 0.5, fontSize: 9, fontWeight: 600, color: mcl.muted, textTransform: 'uppercase', borderBottom: `1px solid ${mcl.border}` }}>
        <Box>Project</Box><Box>In</Box><Box>Out</Box><Box>Hours</Box>
      </Box>
      {[
        { project: 'Riverside Dev', i: '07:45', o: '—', h: '4h 32m', active: true },
      ].map(r => (
        <Box key={r.project} sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.6fr 0.6fr', gap: 1, py: 0.8, fontSize: 11, borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
          <Box sx={{ fontWeight: 500 }}>{r.project}</Box>
          <Box>{r.i}</Box>
          <Box>{r.o}</Box>
          <Box sx={{ fontWeight: 600, color: r.active ? mcl.primary : mcl.text }}>{r.h}</Box>
        </Box>
      ))}
    </Box>
    {/* Week summary */}
    <Box sx={{ mx: 2, mb: 1.5, mt: 0.5, borderRadius: 2, background: '#f0f9ff', border: '1px solid #bae6fd', p: 1.2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ fontSize: 11, fontWeight: 600, color: mcl.primary }}>This week</Box>
        <Box sx={{ fontSize: 13, fontWeight: 700, color: mcl.primary }}>38h 17m</Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.8 }}>
        {['M', 'T', 'W', 'T', 'F'].map((d, i) => (
          <Box key={d + i} sx={{ flex: 1, textAlign: 'center', py: 0.3, borderRadius: 1, background: i < 4 ? '#dcfce7' : '#e0f2fe', fontSize: 9, fontWeight: 600, color: i < 4 ? '#166534' : mcl.primary }}>
            {d}: {['8.5', '8.75', '7.5', '9.0', '4.5'][i]}h
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export default function SalesLanding() {
  const theme = useTheme();

  const [demoOpen, setDemoOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ email: '', date: null, time: null });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [demoError, setDemoError] = useState('');

  const handleDemoChange = (e) => {
    const { name, value } = e.target;
    setDemoForm(prev => ({ ...prev, [name]: value }));
    if (demoError) setDemoError('');
  };

  const handleDemoDateChange = (newDate) => {
    setDemoForm(prev => ({ ...prev, date: newDate }));
    if (demoError) setDemoError('');
  };

  const handleDemoTimeChange = (newTime) => {
    setDemoForm(prev => ({ ...prev, time: newTime }));
    if (demoError) setDemoError('');
  };

  const handleDemoClose = () => {
    if (demoLoading) return;
    setDemoOpen(false);
    setTimeout(() => {
      setDemoForm({ email: '', date: null, time: null });
      setDemoSuccess(false);
      setDemoError('');
    }, 300);
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.email.trim() || !demoForm.date || !demoForm.time) {
      setDemoError('Please fill in all fields');
      return;
    }
    setDemoLoading(true);
    setDemoError('');
    try {
      const formattedDate = demoForm.date.format('dddd, D MMMM YYYY');
      const formattedTime = demoForm.time.format('HH:mm');
      const enquiry = `Demo request — ${formattedDate} at ${formattedTime}`;
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: demoForm.email, enquiry }),
      });
      const data = await response.json();
      if (response.ok) {
        setDemoSuccess(true);
      } else {
        setDemoError(data.error || 'Failed to submit. Please try again.');
      }
    } catch {
      setDemoError('Network error. Please try again.');
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PublicPageShell>
      <Helmet>
        <title>Struxor — Construction Software for Quotes, Projects & Site Collaboration</title>
        <meta name="description" content="Struxor is construction software for managing quotes, projects, documents, and on-site collaboration. AI-powered assistant, digital forms, timesheets, and offline access. Start your free trial." />
        <link rel="canonical" href="https://www.struxorapp.com/" />
        <meta property="og:title" content="Struxor — Construction Software for Quotes, Projects & Site Collaboration" />
        <meta property="og:description" content="Struxor is construction software for managing quotes, projects, documents, and on-site collaboration. AI-powered assistant, digital forms, timesheets, and offline access. Start your free trial." />
        <meta property="og:url" content="https://www.struxorapp.com/" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Struxor',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          description: 'Construction software for managing quotes, projects, documents, and on-site collaboration with an AI-powered assistant that takes action.',
          url: 'https://www.struxorapp.com/',
          downloadUrl: [
            'https://apps.apple.com/gb/app/struxor/id6759628255',
            'https://play.google.com/store/apps/details?id=com.struxorapp.struxor'
          ]
        })}</script>
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' }, // Simple CSS parallax
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: theme.palette.mode === 'light' 
              ? 'linear-gradient(180deg, rgba(17,24,39,0.45) 0%, rgba(17,24,39,0.6) 60%, rgba(17,24,39,0.75) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.8) 60%, rgba(0,0,0,0.9) 100%)',
            zIndex: 0,
          },
        }}
      >
        <Container sx={{ 
          py: { xs: 8, md: 16 }, 
          textAlign: 'center', 
          color: '#ffffff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: { xs: '60vh', md: '70vh' },
          zIndex: 2,
        }}>
          
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Box 
              component="img" 
              src={LOGO_SRC} 
              alt="Struxor construction software logo" 
              sx={{ 
                height: { xs: 60, sm: 80, md: 100 },
                maxWidth: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)', // Always white logo on hero
              }} 
            />
          </Box>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: { xs: 0, md: '-0.5px' },
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.25rem', lg: '3.75rem' },
              mb: { xs: 2, md: 3 },
            }}
          >
            Construction Management Software
          </Typography>
          <Typography 
            variant="subtitle1"
            component="p"
            color="rgba(255,255,255,0.95)"
            sx={{ 
              maxWidth: 820, 
              mx: 'auto', 
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '1rem', md: '1.125rem' },
              px: { xs: 1, sm: 0 },
            }}
          >
            Whether you're in the office or on the jobsite, Struxor keeps your entire team aligned with real‑time project data, powerful document management, and an AI assistant that takes action.
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            sx={{ mb: { xs: 2, md: 3 } }}
          >
            <Button 
              component={RouterLink} 
              to="/register" 
              variant="contained" 
              color="primary"
              size="medium"
              sx={{ 
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.25 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                borderRadius: '50px',
                boxShadow: '0 8px 24px rgba(0, 135, 193, 0.3)',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0, 135, 193, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Start Free Trial
            </Button>
            
            <Button
              onClick={() => setDemoOpen(true)}
              variant="outlined"
              size="medium"
              startIcon={<CalendarMonthIcon />}
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.25 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                borderRadius: '50px',
                borderColor: 'rgba(255,255,255,0.7)',
                color: '#ffffff',
                backgroundColor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Book a Demo
            </Button>
          </Stack>

          {/* Store links */}
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 4, textAlign: 'center' }}>
            Available on{' '}
            <Box component="a" href="https://apps.apple.com/gb/app/struxor/id6759628255" target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#fff', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } }}>
              <AppleIcon sx={{ fontSize: '1rem' }} />
              App Store
            </Box>{' '}
            &{' '}
            <Box component="a" href="https://play.google.com/store/apps/details?id=com.struxorapp.struxor" target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#fff', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } }}>
              <AndroidIcon sx={{ fontSize: '1rem' }} />
              Google Play
            </Box>
          </Typography>

        </Container>
      </Box>

      

      {/* Features */}
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Typography 
          variant="h4" 
          component="h2"
          align="center" 
          gutterBottom
          sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
        >
          Everything your construction team needs in one platform
        </Typography>
        <Typography 
          variant="body1" 
          align="center" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 4, md: 6 },
            px: { xs: 2, sm: 0 },
            fontSize: { xs: '0.95rem', md: '1rem' },
            maxWidth: 750,
            mx: 'auto',
          }}
        >
          Manage your full project lifecycle from quote through to completion. Save time, reduce errors, and improve collaboration across your entire organisation.
        </Typography>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Feature icon={SmartToyIcon} title="AI from the Ground Up">
              Inteligence embeded across the platform — from Quote Assist ensuring you haven't missed a thing, to a first draft of RAMS for competent review, and everything in between.
            </Feature>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Feature icon={AssignmentIcon} title="Project Management">
              Manage your full project lifecycle from quote through to completion. Keep every stakeholder on the same page.
            </Feature>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Feature icon={PictureAsPdfIcon} title="Digital Docs & PDF Export">
              Quotes, RAMS, Purchase Orders, custome forms, and more — all exportable as professional PDFs with your branding and always available.
            </Feature>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Feature icon={AccessTimeIcon} title="Timesheets & Time Tracking">
              Clock in and out from the field. Submit for approval with weekly summaries broken down by day. Managers review and approve team member timesheets.
            </Feature>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Feature icon={PhotoCameraIcon} title="Site Photos & Plan Markup">
              Capture and upload site photos directly from your device. View construction plans with interactive pin markup to create linked activities.
            </Feature>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Feature icon={CloudOffIcon} title="Works Offline">
              Create activities, upload photos, complete forms, and capture signatures even without internet — everything syncs automatically when you're back online.
            </Feature>
          </Grid>
        </Grid>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3, maxWidth: 820, mx: 'auto', lineHeight: 1.7 }}>
          {AI_RAMS_DISCLAIMER}
        </Typography>
        <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 5 } }}>
          <Button
            component={RouterLink}
            to="/features"
            variant="outlined"
            size="large"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            See all features →
          </Button>
        </Box>
      </Container>

{/* AI Assistance Examples */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : alpha(theme.palette.background.paper, 0.5), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Typography 
            variant="h4" 
            component="h2"
            align="center" 
            gutterBottom
            sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
          >
            Next generation AI assistance — actions, not answers
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 2, sm: 0 },
              fontSize: { xs: '0.95rem', md: '1rem' }
            }}
          >
            Real requests from construction teams using Struxor.
          </Typography>
          
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: { xs: 500, md: 300 },
              mb: { xs: 3, md: 4 }
            }}
          >
            <Card
              elevation={0}
              sx={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                maxWidth: 860,
                width: '100%',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
              }}
            >
              <ChatTypePairs
                pairs={[
                  {
                    request: 'Create a Permit to Dig form for our team.',
                    response: 'I can set up a reusable “Permit to Dig” form with fields for permit details, service checks, known services, controls, people, signatures, and photos. Want to add any extra checks before I publish?'
                  },
                  {
                    request: 'Create a quote for SampleCo Construction with the reference of Enabling Works. Add the following items from saved items at average rates: 180m of 2m high v-mesh, 2 x additional posts, 250m of upvc hoarding on ground, 7 x additional post.',
                    response: 'Quote created for SampleCo Construction, ref: Enabling Works. Items added: 2.0m high V-Mesh Weldmesh: 180 m @ £80.00/m; Additional Posts: 2 @ £100.00 each; 2.4m high UPVC hoarding: 250 m @ £160.00/m; Additional Posts: 7 @ £100.00 each. Please confirm the site address.'
                  },
                  {
                    request: 'Add a new user emma@sampleco.com.',
                    response: 'I can add Emma. What role should she have: Office or Site? Her display name will be Emma unless you specify otherwise. Notifications will be on and the created date set to now.'
                  },
                ]}
                typingSpeed={25}
                pauseBetweenUserAndAssistant={650}
                pauseBetweenPairs={3000}
              />
            </Card>
          </Box>

          {/* Tiles removed as requested */}
        </Container>
      </Box>

      

      {/* Pricing teaser */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f9fafb' : theme.palette.background.default, borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Typography 
            variant="h4" 
            component="h2"
            align="center" 
            gutterBottom
            sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
          >
            Pricing that scales with you
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 2, sm: 0 },
              fontSize: { xs: '0.95rem', md: '1rem' }
            }}
          >
            Flat monthly pricing. No per-user fees. No annual contracts. Every plan starts with a 14‑day free trial.
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {[
              { name: 'Starter', price: '£40', bullets: ['Up to 2 users', '100 GB storage', '300 monthly AI requests'] },
              { name: 'Team', price: '£200', bullets: ['Up to 8 users', '1 TB storage', '1,500 monthly AI requests'] },
              { name: 'Organisation', price: '£800', bullets: ['Up to 35 users', '3 TB storage', '5,000 monthly AI requests'] },
              { name: 'Enterprise', price: 'Competitive', bullets: ['Custom users & storage', 'Dedicated support', 'Advanced features'] },
            ].map((plan) => (
              <Grid key={plan.name} item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  borderRadius: 4, 
                  border: plan.popular ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`, 
                  background: theme.palette.background.paper, 
                  transition: 'transform 200ms ease, box-shadow 200ms ease', 
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 24px rgba(0,0,0,0.08)' } 
                }}>
                  <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>{plan.name}</Typography>
                      {plan.popular && <Chip color="primary" size="small" label="Most popular" />}
                    </Stack>
                    <Typography variant="h4" sx={{ mb: 2, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                      {plan.price}
                      {plan.price !== 'Competitive' && <Typography component="span" variant="subtitle2">/month</Typography>}
                    </Typography>
                    <Stack spacing={1.2} sx={{ color: 'text.secondary' }}>
                      {plan.bullets.map(b => (
                        <Stack direction="row" spacing={1} alignItems="center" key={b}>
                          <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>{b}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 5 } }}>
            <Button
              component={RouterLink}
              to="/pricing"
              variant="outlined"
              size="large"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 4,
                borderColor: 'primary.main',
                color: 'primary.main',
              }}
            >
              View full pricing & compare →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ background: 'linear-gradient(135deg, #0087c1 0%, #005a8c 100%)', py: { xs: 6, md: 8 }, textAlign: 'center', color: '#fff' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Ready to streamline your construction projects?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, lineHeight: 1.6 }}>
            14-day free trial. Unlimited users and projects. No credit card required.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                background: '#fff',
                color: '#0087c1',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                '&:hover': { background: alpha('#fff', 0.9) },
              }}
            >
              Start Free Trial
            </Button>
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
              }}
            >
              Get in Touch
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : alpha(theme.palette.background.paper, 0.5), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Typography 
            variant="h4" 
            component="h2"
            align="center" 
            gutterBottom
            sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
          >
            Frequently asked questions
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 2, sm: 0 },
              fontSize: { xs: '0.95rem', md: '1rem' }
            }}
          >
            Common questions about Struxor construction software.
          </Typography>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {[
              {
                q: 'What is Struxor?',
                a: 'Struxor is construction software built for teams of all sizes who need to manage quotes, projects, documents, and on-site collaboration from anywhere. It includes an AI assistant, digital forms, timesheets, purchase orders, RAMS management, site photos with plan markup, and offline access.'
              },
              {
                q: 'Does Struxor work offline?',
                a: 'Yes. Struxor syncs your project data for offline access. You can create activities, upload photos, complete forms, and capture signatures even without an internet connection. Everything syncs automatically when you\'re back online.'
              },
              {
                q: 'How much does Struxor cost?',
                a: 'Plans start at £40/month for up to 2 users. The Team plan is £200/month for up to 8 users, the Organisation plan is £800/month for up to 35 users, and Enterprise pricing is available for larger teams. Every plan starts with a 14-day free trial — no credit card required. Prices exclude VAT.'
              },
              {
                q: 'Does Struxor have a mobile app?',
                a: 'Yes. Struxor has native mobile apps for iOS and Android, built specifically for on-site use. Clock in and out, complete forms, upload site photos, capture signatures, and manage activities — all from your phone, even offline.'
              },
            ].map((faq, idx) => (
              <Accordion 
                key={idx}
                elevation={0}
                sx={{ 
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '12px !important',
                  mb: 1.5,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { mb: 1.5 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                  {faq.q}
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
      {/* Book Demo Dialog */}
      <Dialog
        open={demoOpen}
        onClose={handleDemoClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
          Book a Demo
          <IconButton
            onClick={handleDemoClose}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {demoSuccess ? (
            <Alert severity="success" sx={{ my: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Demo request received!</Typography>
              <Typography variant="body2">
                We'll be in touch to confirm your slot.
              </Typography>
            </Alert>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box component="form" id="demo-form" onSubmit={handleDemoSubmit}>
                <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                  {demoError && (
                    <Alert severity="error" sx={{ fontSize: '0.875rem' }}>{demoError}</Alert>
                  )}
                  <TextField
                    name="email"
                    type="email"
                    label="Your email address"
                    value={demoForm.email}
                    onChange={handleDemoChange}
                    required
                    fullWidth
                    disabled={demoLoading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <DatePicker
                    label="Preferred date"
                    value={demoForm.date}
                    onChange={handleDemoDateChange}
                    disabled={demoLoading}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                  />
                  <TimePicker
                    label="Preferred time"
                    value={demoForm.time}
                    onChange={handleDemoTimeChange}
                    disabled={demoLoading}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                  />
                </Stack>
              </Box>
            </LocalizationProvider>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {demoSuccess ? (
            <Button onClick={handleDemoClose} variant="contained" fullWidth sx={{ borderRadius: 2, py: 1 }}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleDemoClose} disabled={demoLoading} sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="demo-form"
                variant="contained"
                disabled={demoLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  background: 'linear-gradient(135deg, #0087c1 0%, #0066a1 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #0066a1 0%, #004d81 100%)' },
                }}
                startIcon={demoLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {demoLoading ? 'Sending…' : 'Request Demo'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      </PublicPageShell>
    </LocalizationProvider>
  );
}
